import React from 'react';
import styled from 'styled-components';
import SEO from '../components/SEO';

const NavbarSpacer = styled.div`
  height: 120px;
  @media (min-width: 768px) {
    height: 140px;
  }
  @media (min-width: 1024px) {
    height: 160px;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #fff;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 16px;
  
  @media (min-width: 768px) {
    padding: 60px 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 80px 24px;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
  
  @media (min-width: 768px) {
    font-size: 48px;
  }
`;

const Section = styled.section`
  margin-bottom: 50px;
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 20px;
  margin-top: 40px;
  
  @media (min-width: 768px) {
    font-size: 32px;
  }
`;

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 20px;
`;

const HighlightBox = styled.div`
  background: #000;
  color: #fff;
  padding: 40px;
  border-radius: 8px;
  margin: 40px 0;
  text-align: center;
`;

const HighlightText = styled.p`
  font-size: 24px;
  font-weight: 600;
  line-height: 1.6;
  margin: 0;
  
  @media (min-width: 768px) {
    font-size: 28px;
  }
`;

const QuoteBox = styled.div`
  background: #f5f5f5;
  border-left: 4px solid #000;
  padding: 30px;
  margin: 40px 0;
  border-radius: 4px;
`;

const QuoteText = styled.p`
  font-size: 20px;
  font-weight: 500;
  font-style: italic;
  line-height: 1.8;
  color: #000;
  margin: 0;
  
  @media (min-width: 768px) {
    font-size: 24px;
  }
`;

const List = styled.ul`
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 20px;
  padding-left: 24px;
`;

const ListItem = styled.li`
  margin-bottom: 12px;
`;

const AboutUsPage = () => {
  return (
    <PageContainer>
      <SEO 
        title="About Us"
        description="Learn about TheMadZone - a premium streetwear brand breaking the ordinary. Discover our mission, values, and commitment to individuality, quality, and urban fashion culture."
        keywords="about TheMadZone, streetwear brand, urban fashion, fashion company, TheMadZone story"
      />
      <NavbarSpacer />
      <ContentWrapper>
        <Title>About TheMadZone</Title>

        <Section>
          <Paragraph>
            At TheMadZone, fashion is not just about clothing—it's about attitude.
          </Paragraph>
          <Paragraph>
            We believe the way you dress reflects the way you think. Every outfit carries a mindset, and that mindset shapes your character. What you wear isn't ordinary, and neither are you.
          </Paragraph>
        </Section>

        <HighlightBox>
          <HighlightText>
            TheMadZone is more than a fashion brand.<br />
            It's a mindset.<br />
            It's an attitude.<br />
            It's a way of life.
          </HighlightText>
        </HighlightBox>

        <Section>
          <SectionTitle>Break the Ordinary</SectionTitle>
          <Paragraph>
            Driven by the belief to <strong>Break the Ordinary</strong>, TheMadZone creates apparel for individuals who dare to stand apart. Our designs are bold, expressive, and rooted in confidence—made for those who refuse to blend in and choose to define their own identity.
          </Paragraph>
          <Paragraph>
            We don't chase trends; we create statements. Each piece is designed to represent strength, individuality, and a fearless attitude that speaks without words. From everyday wear to standout styles, our clothing empowers you to own who you are.
          </Paragraph>
        </Section>

        <QuoteBox>
          <QuoteText>
            "What you wear isn't ordinary, and neither are you."
          </QuoteText>
        </QuoteBox>

        <Section>
          <SectionTitle>Our Philosophy</SectionTitle>
          <Paragraph>
            At TheMadZone, we understand that fashion is a form of self-expression. Every garment we create is infused with the spirit of rebellion against the mundane, the ordinary, and the expected. We believe that when you wear TheMadZone, you're not just putting on clothes—you're making a statement about who you are and who you aspire to be.
          </Paragraph>
          <Paragraph>
            Our philosophy is simple: <strong>Be bold. Be different. Be you.</strong> We design for the trailblazers, the rule-breakers, and the dreamers who know that true style comes from within and radiates outward.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>What Makes Us Different</SectionTitle>
          <List>
            <ListItem>
              <strong>Bold Designs:</strong> We create pieces that make a statement, not just follow one. Every design is crafted to help you express your unique personality.
            </ListItem>
            <ListItem>
              <strong>Quality Craftsmanship:</strong> We believe in creating clothing that lasts. Our pieces are made with attention to detail and quality materials that stand the test of time.
            </ListItem>
            <ListItem>
              <strong>Individuality First:</strong> We don't believe in one-size-fits-all fashion. Our collections are designed to help you express your unique style and break free from the ordinary.
            </ListItem>
            <ListItem>
              <strong>Confidence Building:</strong> Our clothing is designed to make you feel powerful, confident, and unapologetically yourself. When you wear TheMadZone, you wear confidence.
            </ListItem>
            <ListItem>
              <strong>Community Driven:</strong> TheMadZone is more than a brand—it's a community of individuals who share the same values of breaking boundaries and expressing themselves authentically.
            </ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>Our Mission</SectionTitle>
          <Paragraph>
            Our mission is to empower individuals to express themselves authentically through fashion. We create clothing that serves as a canvas for self-expression, allowing you to tell your story without saying a word. Whether you're navigating the urban landscape or making your mark in the world, TheMadZone is here to support your journey.
          </Paragraph>
          <Paragraph>
            We're committed to creating a brand that stands for something more than just clothing. We stand for individuality, confidence, and the courage to be different. Every piece we create is a testament to our belief that fashion should empower, inspire, and transform.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Join the Movement</SectionTitle>
          <Paragraph>
            When you shop at TheMadZone, you're not just buying clothes—you're joining a movement. A movement of individuals who refuse to conform, who choose to stand out, and who believe that their style should reflect their unique identity.
          </Paragraph>
          <Paragraph>
            From our carefully curated collections to our commitment to quality and individuality, every aspect of TheMadZone is designed with you in mind. We invite you to explore our collections, find pieces that speak to you, and join us in breaking the ordinary.
          </Paragraph>
        </Section>

        <HighlightBox>
          <HighlightText>
            TheMadZone — Break the Ordinary
          </HighlightText>
        </HighlightBox>

        <Section>
          <Paragraph style={{ textAlign: 'center', fontSize: '18px', fontWeight: 500 }}>
            Ready to break the ordinary? <a href="/shop" style={{ color: '#000', textDecoration: 'underline' }}>Explore our collections</a> and find pieces that define who you are.
          </Paragraph>
        </Section>
      </ContentWrapper>
    </PageContainer>
  );
};

export default AboutUsPage;

